import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { platformApi } from '../../api/platform';
import { useAuth } from '../../auth/AuthProvider';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';

const emptyAdminForm = { email: '', first_name: '', last_name: '', password: '' };

export default function ShopDetailPage() {
  const { shopId } = useParams();
  const { getToken } = useAuth();
  const [shop, setShop] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [shopName, setShopName] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyAdminForm);
  const [saving, setSaving] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [shopData, adminData] = await Promise.all([
        platformApi.getShop(token, shopId),
        platformApi.listShopAdmins(token, shopId),
      ]);
      setShop(shopData);
      setShopName(shopData.name);
      setCustomDomain(shopData.custom_domain ?? '');
      setAdmins(adminData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, shopId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveShop = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const updated = await platformApi.updateShop(token, shopId, {
        name: shopName.trim(),
        custom_domain: customDomain.trim() || null,
      });
      setShop(updated);
      setCustomDomain(updated.custom_domain ?? '');
      setSuccess('Shop settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const updated = await platformApi.updateShop(token, shopId, {
        is_active: !shop.is_active,
      });
      setShop(updated);
      setSuccess(updated.is_active ? 'Shop activated.' : 'Shop deactivated.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setCreatedPassword(null);
    try {
      const token = await getToken();
      const payload = {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
      };
      if (form.password) {
        payload.password = form.password;
      }
      const created = await platformApi.assignShopAdmin(token, shopId, payload);
      setModalOpen(false);
      setForm(emptyAdminForm);
      if (created.temporary_password) {
        setCreatedPassword({
          email: created.user?.email || form.email,
          password: created.temporary_password,
        });
      }
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this shop admin?')) {
      return;
    }
    setError(null);
    try {
      const token = await getToken();
      await platformApi.removeShopAdmin(token, shopId, userId);
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!shop) {
    return <EmptyState message="Shop not found." />;
  }

  return (
    <>
      <PageHeader
        title={shop.name}
        description={
          <>
            Schema <span className="schema-pill">{shop.schema_name}</span> · slug{' '}
            <strong>{shop.slug}</strong>
          </>
        }
        actions={
          <>
            <Link to="/platform/shops" className="btn btn-secondary">
              Back to shops
            </Link>
            <button type="button" className="btn btn-secondary" onClick={handleToggleActive} disabled={saving}>
              {shop.is_active ? 'Deactivate' : 'Activate'}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
              Assign admin
            </button>
          </>
        }
      />

      <ErrorBanner message={error} />
      {success && <div className="success-banner">{success}</div>}

      {createdPassword && (
        <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--accent)' }}>
          <div className="card-body">
            <strong>Shop admin account ready.</strong> Share these credentials so they can sign in at{' '}
            <code>/shop</code>.
            <p style={{ margin: '0.75rem 0 0' }}>
              Email: <strong>{createdPassword.email}</strong>
              <br />
              Temporary password: <strong>{createdPassword.password}</strong>
            </p>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Shop settings</h3>
          <form className="form-grid" onSubmit={handleSaveShop}>
            <div className="form-field">
              <label htmlFor="shop-name">Shop name</label>
              <input
                id="shop-name"
                value={shopName}
                onChange={(event) => setShopName(event.target.value)}
                disabled={saving}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="custom-domain">Custom domain</label>
              <input
                id="custom-domain"
                value={customDomain}
                onChange={(event) => setCustomDomain(event.target.value)}
                placeholder="shop.example.com"
                disabled={saving}
              />
            </div>
            <div className="dns-record-hint">
              Storefront:{' '}
              <a href={shop.storefront_url} target="_blank" rel="noreferrer">
                {shop.storefront_url}
              </a>
              {shop.custom_domain && (
                <>
                  {' '}
                  · Custom domain status: <code>{shop.domain_status || 'pending'}</code>
                </>
              )}
            </div>
            <div className="page-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save shop settings'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Shop admins</h3>
          {admins.length === 0 ? (
            <EmptyState message="No shop admins assigned. Assign an admin so they can access the shop dashboard." />
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>
                      {admin.user
                        ? `${admin.user.first_name} ${admin.user.last_name}`
                        : `User #${admin.user_id}`}
                    </td>
                    <td>{admin.user?.email}</td>
                    <td>
                      <span className="badge badge-info">{admin.role}</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemove(admin.user_id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        title="Assign shop admin"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" form="assign-admin-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Assigning…' : 'Assign admin'}
            </button>
          </>
        }
      >
        <form id="assign-admin-form" className="form-grid" onSubmit={handleAssign}>
          <div className="form-field">
            <label htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              required
            />
          </div>
          <div className="form-grid two-col">
            <div className="form-field">
              <label htmlFor="admin-first">First name</label>
              <input
                id="admin-first"
                value={form.first_name}
                onChange={(event) => setForm((prev) => ({ ...prev, first_name: event.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="admin-last">Last name</label>
              <input
                id="admin-last"
                value={form.last_name}
                onChange={(event) => setForm((prev) => ({ ...prev, last_name: event.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="admin-password">Password (optional)</label>
            <input
              id="admin-password"
              type="password"
              value={form.password}
              onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
              placeholder="Leave blank to auto-generate a temporary password"
              minLength={8}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}
